import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { App } from 'antd';
import { RollbackOutlined, FieldTimeOutlined, DownloadOutlined, UploadOutlined, FormOutlined, } from '@ant-design/icons';
import { downloadTextAsFile, downloadTextFormat, } from 'screens/DashboardScreen/pages/EditorPage/ProjectMenuPanel/exports';
import { txt } from 'libs/i18n';
import * as ProjectAPI from 'api/project-api';
import { useSession } from 'components/Authenticated';
import ConfirmImportTrsxModal from 'components/ConfirmImportTrsxModal';
import { disabledGuards } from '../disabledGuards';
import ControlsButton from '../ControlsButton';
import { useEditorContext } from '../../contexts/projectSignal';
import { savingStateSignal } from '../../signalsStore/savingState';
const selectFiles = (accept) => new Promise((resolve) => {
    const filesInput = document.createElement('input');
    filesInput.type = 'file';
    filesInput.accept = accept;
    filesInput.onchange = () => resolve(filesInput.files);
    filesInput.click();
});
const ProjectMenuAdvanced = ({ editorController, disabledChecks, buttonClassName, }) => {
    const { session } = useSession();
    const { notification, message } = App.useApp();
    const { projectSignal, logger } = useEditorContext();
    const [uploadTrsxModal, setUploadTrsxModal] = useState({
        isVisible: false, trsx: '', language: null,
    });
    const [isVisibleResetTrsxModal, setIsVisibleResetTrsxModal] = useState(false);
    const alignDocument = () => {
        try {
            editorController.alignDocument();
            void message.success(txt('alignmentSuccess'));
            logger.info('Document aligned successfully');
        }
        catch (error) {
            logger.error('Failed to align document', {}, error);
            notification.error({
                message: txt('alignmentFail'),
                placement: 'topRight',
                duration: 3,
            });
        }
    };
    const handleDownloadText = async (format) => {
        const projectName = projectSignal.value.description.name;
        if (format === 'trsx' && savingStateSignal.value === 'saved') {
            const trsxResult = await ProjectAPI.fetchTrsx(session.connection, projectSignal.value.id, 'currentTrsx');
            if (trsxResult.isSuccess()) {
                downloadTextAsFile(`${projectName}.trsx`, trsxResult.get().content, 'text/xml');
                logger.info('CurrentTrsx downloaded from server');
                return;
            }
            const initialTrsx = await ProjectAPI.fetchTrsx(session.connection, projectSignal.value.id, 'initialTrsx');
            if (initialTrsx.isSuccess()) {
                downloadTextAsFile(`${projectName}.trsx`, initialTrsx.get().content, 'text/xml');
                logger.info('InitialTrsx downloaded from server');
                return;
            }
            logger.error('Failed to download TRSX from server', {}, trsxResult.err());
        }
        const documentContent = editorController.exportDocument();
        downloadTextFormat(documentContent, projectName, format);
        logger.info('Document exported from editor', { format });
    };
    const handleUploadTrsx = async () => {
        var _a;
        const files = await selectFiles('.trsx');
        if (files === null || files.length === 0) {
            logger.info('TRSX upload cancelled - no file selected');
            return;
        }
        const trsx = await files[0].text();
        const language = ProjectAPI.getLanguageFromTrsx(trsx);
        logger.info('TRSX file selected for upload', {
            fileName: files[0].name,
            language: (_a = language === null || language === void 0 ? void 0 : language.code) !== null && _a !== void 0 ? _a : 'Unknown',
        });
        setUploadTrsxModal({ isVisible: true, trsx, language });
    };
    const uploadTrsx = (selectedLanguage) => {
        var _a, _b, _c;
        try {
            const language = (_a = uploadTrsxModal.language) !== null && _a !== void 0 ? _a : selectedLanguage;
            logger.info('Uploading TRSX', {
                language: (_b = language === null || language === void 0 ? void 0 : language.code) !== null && _b !== void 0 ? _b : 'Unknown',
            });
            void editorController.importTrsx({
                content: uploadTrsxModal.trsx,
                etag: editorController.etag,
                label: editorController.label,
                language,
            }, true, projectSignal.value, false);
            void message.success(txt('trsxUploaded'));
            logger.info('TRSX uploaded successfully', {
                language: (_c = language === null || language === void 0 ? void 0 : language.code) !== null && _c !== void 0 ? _c : 'Unknown',
            });
        }
        catch (error) {
            void message.error(txt('trsxUploadFail'));
            logger.error('Failed to import transcription', {}, error);
        }
    };
    const resetTranscription = async () => {
        var _a;
        logger.info('Resetting transcription to initial state');
        const trsx = await ProjectAPI.fetchTrsx(session.connection, projectSignal.value.id, 'initialTrsx');
        if (trsx.isSuccess()) {
            const language = ProjectAPI.getLanguageFromTrsx(trsx.get().content);
            editorController.importTrsx({
                content: trsx.get().content,
                etag: editorController.etag,
                label: editorController.label,
                language,
            }, true, projectSignal.value, false);
            void message.success(txt('resetSuccessful'));
            logger.info('Transcription reset successfully', {
                language: (_a = language === null || language === void 0 ? void 0 : language.code) !== null && _a !== void 0 ? _a : 'Unknown',
            });
        }
        else {
            void message.error(txt('resetFailed'));
            logger.error('Failed to reset transcription', {}, trsx.err());
        }
    };
    const activateSectionHeadings = async () => {
        logger.info('Activating section headings');
        const result = await ProjectAPI.updateProjectHeadingsMetadata(session.connection, projectSignal.value, {
            pattern: '^\\d{1,3} .*$',
            options: [
                { name: '128 nadpis varianta první', id: '0' },
                { name: '234 nadpis varianta druhá', id: '1' },
            ],
        });
        if (result.isSuccess()) {
            void message.success(txt('done'));
            logger.info('Section headings activated successfully');
            window.location.reload();
        }
        else {
            void message.error(txt('unexpectedError'));
            logger.error('Failed to activate section headings', {}, result.err());
        }
    };
    return (_jsxs(_Fragment, { children: [_jsx(ControlsButton, { icon: _jsx(DownloadOutlined, {}), className: buttonClassName, label: txt('dlTrsx'), busyMessage: `${txt('downloading')}…`, action: () => handleDownloadText('trsx'), disabledMessage: disabledGuards([
                    disabledChecks.editorHasContent,
                ]) }), _jsx(ControlsButton, { icon: _jsx(DownloadOutlined, {}), className: buttonClassName, label: txt('dlBdd'), accessClaim: "editor:subtitleMode", busyMessage: `${txt('downloading')}…`, action: () => handleDownloadText('bdd'), disabledMessage: disabledGuards([
                    disabledChecks.editorHasContent,
                ]) }), _jsx(ControlsButton, { icon: _jsx(UploadOutlined, {}), className: buttonClassName, label: txt('upTranscript'), action: handleUploadTrsx, disabledMessage: disabledGuards([
                    disabledChecks.mediaProcessingComplete,
                    disabledChecks.readOnly,
                    disabledChecks.isLocked,
                ]) }), _jsx(ControlsButton, { icon: _jsx(FieldTimeOutlined, {}), className: buttonClassName, label: txt('alignAll'), action: alignDocument, disabledMessage: disabledGuards([
                    disabledChecks.editorHasContent,
                    disabledChecks.mediaProcessingComplete,
                    disabledChecks.readOnly,
                    disabledChecks.isLocked,
                ]) }), _jsx(ControlsButton, { icon: _jsx(RollbackOutlined, {}), className: buttonClassName, label: txt('reset'), action: () => setIsVisibleResetTrsxModal(true), disabledMessage: disabledGuards([
                    disabledChecks.mediaProcessingComplete,
                    disabledChecks.readOnly,
                    disabledChecks.isLocked,
                ]) }), session.login.hasClaim('editor:sectionHeadingsTesting') && (_jsx(ControlsButton, { icon: _jsx(FormOutlined, {}), className: buttonClassName, label: txt('sectionHeadingsTesting'), action: activateSectionHeadings, disabledMessage: disabledGuards([
                    disabledChecks.editorHasContent,
                    disabledChecks.mediaProcessingComplete,
                ]) })), uploadTrsxModal.isVisible ? (_jsx(ConfirmImportTrsxModal, { isVisible: uploadTrsxModal.isVisible, asyncAction: (language) => uploadTrsx(language), language: uploadTrsxModal.language, title: txt('uploadTrsxConfirm'), content: txt('changesOverwritten'), okText: txt('yesUpload'), onCancel: () => setUploadTrsxModal(Object.assign(Object.assign({}, uploadTrsxModal), { isVisible: false })) })) : null, isVisibleResetTrsxModal ? (_jsx(ConfirmImportTrsxModal, { isVisible: isVisibleResetTrsxModal, asyncAction: resetTranscription, title: txt('resetTranscription'), content: txt('changesOverwritten'), okText: txt('yesReset'), onCancel: () => setIsVisibleResetTrsxModal(false) })) : null] }));
};
export default ProjectMenuAdvanced;
